import type { FidelityLevel, FidelityReport } from './types';

/**
 * 保真度矩阵：所有结论必须能从「源格式 + 目标格式 + 是否无损编码路径」推导出来，
 * 不允许调用方随口声明 L0。规范要求：PNG→WebP Lossless 最高只能写 L2；
 * 有损源转无损目标要写 L3 并明确「不恢复已丢失信息」。
 */

const SOURCE_LOSSY = new Set(['jpg', 'jpeg', 'webp', 'avif', 'gif', 'bmp']);
const RASTER_TARGETS = new Set(['png', 'jpg', 'webp', 'avif', 'bmp']);

export interface ImageFidelityInput {
  sourceExtension: string;
  targetExtension: string;
  /** 引擎实际使用的编码路径是否为无损。 */
  losslessEncode: boolean;
  hasAlpha?: boolean;
  /** 源文件为多帧（如 GIF）时，仅首帧被转换。 */
  multiFrame?: boolean;
  width?: number;
  height?: number;
}

export function imageFidelity(input: ImageFidelityInput): FidelityReport {
  const source = input.sourceExtension.toLowerCase();
  const target = input.targetExtension.toLowerCase();
  const sizeLabel = input.width && input.height ? `${input.width}×${input.height}` : '像素尺寸';

  if (source === 'svg') {
    return {
      level: 'L3',
      summary: '矢量转位图：可缩放性丢失',
      preserved: ['可见图形', sizeLabel],
      changed: ['渲染为固定像素矩阵'],
      unsupported: ['矢量路径与可缩放性', '文本可选中/可编辑性'],
    };
  }

  if (!RASTER_TARGETS.has(target)) {
    return {
      level: 'L3',
      summary: '非常规图像目标格式，保真度无法保证',
      preserved: [],
      changed: ['编码方式'],
      unsupported: ['当前引擎未定义该目标的保真度保证'],
    };
  }

  const lossySource = SOURCE_LOSSY.has(source);
  const alphaLoss = target === 'jpg' && (input.hasAlpha ?? true);
  const multiFrame = input.multiFrame ? ['其他动画帧（仅转换首帧）'] : [];

  if (input.losslessEncode && !lossySource) {
    return {
      level: 'L2',
      summary: '视觉无损：像素与尺寸保持，编码容器改变',
      preserved: ['像素内容', sizeLabel, ...(input.hasAlpha ? ['透明通道'] : [])],
      changed: ['容器与编码方式', '文件体积'],
      unsupported: [...multiFrame],
    };
  }

  if (lossySource) {
    return {
      level: 'L3',
      summary: input.losslessEncode
        ? '有损源转无损目标：不恢复源编码已丢失的信息'
        : '有损源继续有损编码：画质不会提升',
      preserved: [sizeLabel, ...(input.hasAlpha && target !== 'jpg' ? ['透明通道'] : [])],
      changed: input.losslessEncode ? ['容器与编码方式改为无损路径'] : ['像素量化与压缩', '文件体积'],
      unsupported: ['源编码已丢失的细节（不可恢复）', ...(alphaLoss ? ['透明通道（JPG 不支持）'] : []), ...multiFrame],
    };
  }

  if (!input.losslessEncode) {
    return {
      level: 'L3',
      summary: '有损转换：源已是压缩图像，画质不会提升',
      preserved: [sizeLabel],
      changed: ['像素编码与压缩痕迹', '文件体积'],
      unsupported: ['源编码已丢失的细节（不可恢复）', ...(alphaLoss ? ['透明通道（JPG 不支持）'] : []), ...multiFrame],
    };
  }

  return {
    level: 'L3',
    summary: '有损编码：以画质换取体积',
    preserved: [sizeLabel, ...(input.hasAlpha && target !== 'jpg' ? ['透明通道'] : [])],
    changed: ['像素量化与压缩', '文件体积'],
    unsupported: [...(alphaLoss ? ['透明通道（JPG 不支持）'] : []), ...multiFrame],
  };
}

export interface DocumentFidelityInput {
  sourceExtension: string;
  targetExtension: string;
  /** json→csv 等：结构是否可被目标格式完整承载。 */
  structurePreserved?: boolean;
  /** 表格类源文件：工作表总数。 */
  sheetsTotal?: number;
  /** 表格类源文件：本次实际导出的工作表数。 */
  sheetsExported?: number;
  /** 由 AI 视觉模型转写（扫描件/图片 OCR）：保真度口径单独说明。 */
  aiRecognized?: boolean;
  notes?: string[];
}

const DOCUMENT_PRESERVED_TEXT = ['文本内容', '换行与段落'];
const DOCUMENT_CHANGED_ENCODING = ['文件编码/扩展名'];

export function documentFidelity(input: DocumentFidelityInput): FidelityReport {
  const source = input.sourceExtension.toLowerCase();
  const target = input.targetExtension.toLowerCase();
  const notes = input.notes ?? [];
  const pair = `${source}>${target}`;

  // AI 通道单独口径：内容由模型转写，无论源格式是什么都不该按「本地解析」的结论描述。
  if (input.aiRecognized) {
    return {
      level: 'L3',
      summary: 'AI 识别转写：内容由视觉模型读取，建议逐字校对',
      preserved: ['可见文本内容', '段落与阅读顺序', '表格行列关系（模型尽量保留）'],
      changed: ['文字由模型转写，可能出现个别错字或断行差异'],
      unsupported: ['原始版式与字体', '图片中的非文字元素（照片、印章、手写涂改等）', ...notes],
    };
  }

  if (pair === 'txt>md') {
    return {
      level: 'L1',
      summary: '结构无损：纯文本按 Markdown 解释',
      preserved: DOCUMENT_PRESERVED_TEXT,
      changed: [...DOCUMENT_CHANGED_ENCODING, '文本被按 Markdown 语法解释'],
      unsupported: [],
    };
  }

  if (pair === 'md>txt') {
    return {
      level: 'L3',
      summary: '有损转换：Markdown 语法标记被剥离',
      preserved: ['可见文本内容'],
      changed: ['移除语法标记', '标题与列表层级降级为纯文本'],
      unsupported: ['标题层级', '链接与图片引用语义', '代码块语言标记'],
    };
  }

  if (pair === 'csv>json' || pair === 'tsv>json') {
    return {
      level: 'L1',
      summary: '结构无损：行列结构映射为对象数组',
      preserved: ['行与列的对应关系', '单元格文本', '列顺序'],
      changed: ['所有单元格以字符串承载'],
      unsupported: [],
    };
  }

  if (pair === 'json>csv') {
    if (input.structurePreserved) {
      return {
        level: 'L1',
        summary: '结构无损：扁平对象数组可完整映射为表格',
        preserved: ['字段与列的对应关系', '字段顺序', '标量值'],
        changed: ['数值/布尔以文本承载'],
        unsupported: [],
      };
    }
    return {
      level: 'L3',
      summary: '有损转换：嵌套结构被展平为表格',
      preserved: ['顶层字段'],
      changed: ['嵌套对象展开为路径列', '数组以文本拼接'],
      unsupported: ['原始嵌套层级', '数组内元素顺序的类型语义'],
    };
  }

  if (pair === 'json>yaml' || pair === 'yaml>json') {
    return {
      level: 'L1',
      summary: '结构无损：同数据模型，仅序列化语法不同',
      preserved: ['键值结构', '数组与嵌套层级', '标量类型'],
      changed: ['序列化语法与缩进风格', '注释在部分路径下不保留'],
      unsupported: [],
    };
  }

  if (pair.endsWith('>xml') || pair.startsWith('xml>')) {
    return {
      level: 'L3',
      summary: '有损转换：XML 与 JSON/YAML 数据模型不对等',
      preserved: ['文本值'],
      changed: ['数组与属性表示方式按约定近似映射'],
      unsupported: ['属性/子元素的原始区分', '混排文本节点', '命名空间语义'],
    };
  }

  if (pair === 'docx>txt' || pair === 'docx>md') {
    return {
      level: 'L3',
      summary:
        target === 'md'
          ? '有损转换：正文文本与 Markdown 结构保留，图片与版式丢失'
          : '有损转换：仅提取正文文本',
      preserved: target === 'md' ? ['正文文本', '标题层级', '列表与强调'] : ['正文文本'],
      changed: ['版式、字体与样式被剥离'],
      unsupported: ['图片与浮动对象', '页眉页脚/脚注/批注', '表格样式与合并单元格', ...notes],
    };
  }

  if (pair === 'pdf>txt' || pair === 'pdf>md') {
    return {
      level: 'L3',
      summary: '有损转换：只提取 PDF 文本层，不推断标题层级',
      preserved: ['文本层内容', '阅读顺序'],
      changed: ['按阅读顺序合并为纯文本'],
      unsupported: ['版面与样式', '图片与矢量图形', '表格边框与单元格结构', '扫描件需改用 AI 识别通道', ...notes],
    };
  }

  if (source === 'xlsx' || source === 'xls') {
    const sheetsTotal = input.sheetsTotal ?? 1;
    const sheetsExported = input.sheetsExported ?? 1;
    const dropped = Math.max(0, sheetsTotal - sheetsExported);

    if (target === 'json') {
      return {
        level: dropped > 0 ? 'L3' : 'L1',
        summary: dropped > 0 ? `结构保留：导出了 ${sheetsExported}/${sheetsTotal} 张工作表` : '结构无损：工作表与单元格值映射为 JSON',
        preserved: ['工作表划分', '行列关系', '单元格值'],
        changed: ['日期等格式化的单元格按原始值承载'],
        unsupported: [
          ...(dropped > 0 ? [`其余 ${dropped} 张工作表`] : []),
          '单元格样式与批注',
          '公式表达式（只保留计算结果）',
          ...notes,
        ],
      };
    }

    if (target === 'csv') {
      return {
        level: 'L3',
        summary: dropped > 0 ? `有损转换：仅导出 ${sheetsExported}/${sheetsTotal} 张工作表` : '有损转换：仅保留单元格文本与行列关系',
        preserved: ['单元格文本', '行列关系', '列顺序'],
        changed: ['样式、公式与合并单元格语义降级为纯文本'],
        unsupported: [
          ...(dropped > 0 ? [`其余 ${dropped} 张工作表`] : []),
          '单元格样式与批注',
          '公式表达式（只保留计算结果）',
          ...notes,
        ],
      };
    }
  }

  return {
    level: 'L3',
    summary: '有损转换：目标格式无法完整承载源结构',
    preserved: ['可见文本内容'],
    changed: ['按目标格式重排内容'],
    unsupported: ['源格式特有结构', ...notes],
  };
}

export function maxFidelity(levels: FidelityLevel[]): FidelityLevel {
  if (levels.includes('L3')) return 'L3';
  if (levels.includes('L2')) return 'L2';
  if (levels.includes('L1')) return 'L1';
  return 'L0';
}

export const FIDELITY_LABELS: Record<FidelityLevel, string> = {
  L0: 'L0 字节无损',
  L1: 'L1 结构无损',
  L2: 'L2 视觉无损',
  L3: 'L3 有损',
};

export function fidelityLabel(level: FidelityLevel): string {
  return FIDELITY_LABELS[level] ?? level;
}
